import {
  ContactFormActionsContext,
  ContactFormDataContext,
  useContactFormActionsFromContext,
  useContactFormDataFromContext,
} from 'src/SharedCommonContext/ContactFormContext';
import React, { useMemo, useState } from 'react';
import { USER_TYPES, webUrlAccess } from 'src/common/constants';
import ContactFormHeading from 'src/components/ContactFormComponents/ContactFormHeading';
import Row from 'src/components/Row';
import Col from 'src/components/Col';
import { getTenantGUID } from 'src/Tenants';
import RoleTile from 'src/components/ContactFormComponents/RoleTile/RoleTile';
import '../../styles/icomoon/style.scss';
import './role-tile-selector.scss';
import { IContact } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { getRoleDefinition } from 'src/common/roleRegistry/roleRegistryHelpers';
import ChangeUserRoleDialogue from 'src/components/ContactFormComponents/ChangeUserRoleDialogue/ChangeUserRoleDialogue';
import Callout from 'src/components/InformationCallout/Callout';
import { getRoleDisplayLabel } from 'src/common/roleRegistry/roleRegistryHelpers';

interface RoleTileSelectorProps {
  isFromPropertyOrSuite?: boolean;
  allowedRoles?: string[];
  formType?: string;
  selectedContact?: IContact | null;
  editingExistingContactInTable?: boolean;
  isRoleDataLoading?: boolean;
  onRoleChangeSuccess?: () => void;
}

const RoleTileSelector = ({
  isFromPropertyOrSuite,
  allowedRoles,
  formType,
  selectedContact,
  isRoleDataLoading,
  onRoleChangeSuccess,
}: RoleTileSelectorProps) => {
  const { contactFormData, errors }: ContactFormDataContext =
    useContactFormDataFromContext();

  const { setContactFormData }: ContactFormActionsContext =
    useContactFormActionsFromContext();

  const user = useSelector(selectAuthenticatedUser);

  const tenantId: any = getTenantGUID(webUrlAccess);

  const [isChangeRoleOpen, setIsChangeRoleOpen] = useState<boolean>(false);

  const isSelf: boolean =
    !!user?.id && !!contactFormData?.id && user.id === contactFormData.id;

  const canChangeRole: boolean =
    !!contactFormData?.id &&
    !isSelf &&
    !!getRoleDefinition(contactFormData.userRole);

  const userTypeForExistingContact: string[] = useMemo(() => {
    return USER_TYPES.filter(
      (userType: string) => userType === contactFormData.userRole
    );
  }, [contactFormData.userRole]);

  const handleRoleSelection = (userType: string) => {
    if (userType === contactFormData.userRole) {
      return;
    }

    /* TODO: HACK - Preserve property data when coming from Property Developer page
     * to prevent losing the pre-selected property. Root cause: role change should
     * not clear all form data, but refactoring the entire form state management
     * is out of scope right now. Or figuring out why some properties don't align on the API etc.*/
    const propertyData = isFromPropertyOrSuite
      ? {
          properties: contactFormData.properties,
          propertyId: contactFormData.propertyId,
        }
      : {
          propertyId: '',
          properties: undefined,
        };

    // NEW: Preserve suite data when coming from Suites page.
    const suiteData = isFromPropertyOrSuite
      ? {
          suiteId: contactFormData.suiteId,
          Suites: contactFormData.Suites,
        }
      : {
          suiteId: '',
          Suites: [],
        };

    setContactFormData({
      userRole: userType,
      ...propertyData,
      ...suiteData,
      serviceType: '',
      servicesProvided:
        contactFormData.userRole === userType
          ? contactFormData.servicesProvided
          : [],
      isPrimaryContact: false,
    });
  };

  const isExistingAccount: boolean = isFromPropertyOrSuite
    ? !!contactFormData?.id && formType !== 'New' && isFromPropertyOrSuite
    : !!contactFormData?.id;

  const isStrataManager: boolean =
    contactFormData.userRole === 'Strata Manager';

  const strataManagerLabel: string = getRoleDisplayLabel('Strata Manager');

  const userTypesToShow: string[] = useMemo(() => {
    let types: string[];

    if (isExistingAccount) {
      types = userTypeForExistingContact;
    } else if (allowedRoles && allowedRoles.length > 0) {
      types = USER_TYPES.filter((type: string) => allowedRoles.includes(type));
    } else {
      types = USER_TYPES;
    }

    const COROMANDEL_TENANT_ID = '4b97c605-6d57-4984-87cd-af9058aa0f9f';

    // Only show Admin tile if current user is a Juniper admin.
    if (!user?.isJuniperAdmin || tenantId === COROMANDEL_TENANT_ID) {
      types = types.filter((type: string) => type !== 'Admin');
    }

    return types;
  }, [
    isExistingAccount,
    userTypeForExistingContact,
    allowedRoles,
    user?.isJuniperAdmin,
  ]);

  if (isFromPropertyOrSuite && !selectedContact && formType !== 'New') {
    return <></>;
  }

  return (
    <>
      {errors.userRole && <div className="input__error">{errors.userRole}</div>}
      <ContactFormHeading>Role</ContactFormHeading>
      <div
        className={
          isRoleDataLoading
            ? 'role-tile-selector-tiles role-tile-selector-tiles-loading'
            : 'role-tile-selector-tiles'
        }
        style={{ pointerEvents: contactFormData?.id ? 'none' : undefined }}
      >
        <Row granola>
          {userTypesToShow.map((userType: string) => {
            const isActiveTile: boolean = userType === contactFormData.userRole;

            return (
              <Col granola key={userType} xs="6" md="3">
                <div className="role-tile-selector-tile-wrapper">
                  <RoleTile
                    role={userType}
                    active={isActiveTile}
                    onClick={() => handleRoleSelection(userType)}
                  />
                </div>
              </Col>
            );
          })}
        </Row>
      </div>
      {isStrataManager && (
        <Callout
          variant="info"
          title="Unit assignment happens on the property"
          subtitle={
            isExistingAccount
              ? `To view or change the units and properties this ${strataManagerLabel} is assigned to, open the relevant property and assign them there.`
              : `Create the ${strataManagerLabel} here first, then assign them to units on the relevant property.`
          }
        />
      )}
      {canChangeRole && isChangeRoleOpen && contactFormData.id && (
        <ChangeUserRoleDialogue
          isOpen={isChangeRoleOpen}
          onClose={() => setIsChangeRoleOpen(false)}
          contact={{
            id: contactFormData.id,
            firstname: contactFormData.firstname,
            lastname: contactFormData.lastname,
            profileImage: contactFormData.profileImage ?? null,
            userRole: contactFormData.userRole,
          }}
          onSuccess={(newRole: string) => {
            setContactFormData({ userRole: newRole });
            onRoleChangeSuccess?.();
          }}
        />
      )}
    </>
  );
};

export default RoleTileSelector;
